import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { platformAdminApi } from '@/features/platform-admin/api/platformAdminApi';
import { chatbotAdminApi } from '../../api/chatbotApi';
import type { AiApiKeyStatus, TenantAiKeysResponse } from '../../types/chatbot.types';

function keyStatusLabel(status: AiApiKeyStatus): string {
  if (!status.configured) {
    return 'No configurada';
  }
  switch (status.source) {
    case 'tenant':
      return 'Configurada (tenant)';
    case 'environment':
      return 'Entorno (desarrollo)';
    default:
      return 'Configurada';
  }
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string').slice(0, 30);
}

function mergeModelIds(current: unknown, extra: unknown): string[] {
  const base = asStringArray(current);
  const seen = new Set(base);
  const merged = [...base];
  for (const id of asStringArray(extra)) {
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged.slice(0, 30);
}

type TenantOption = { id: string; name: string };

type TenantAiKeysCardProps = {
  tenantId?: string;
  title?: string;
  onKeysLoaded?: (status: TenantAiKeysResponse | null) => void;
};

export function TenantAiKeysCard({
  tenantId,
  title = 'API keys del tenant',
  onKeysLoaded,
}: TenantAiKeysCardProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(tenantId);
  const [status, setStatus] = useState<TenantAiKeysResponse | null>(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openrouterKey, setOpenrouterKey] = useState('');
  const [localLlmKey, setLocalLlmKey] = useState('');
  const [localBaseUrl, setLocalBaseUrl] = useState('');
  const [customLocalModel, setCustomLocalModel] = useState('');
  const [selectedOpenRouter, setSelectedOpenRouter] = useState<string[]>([]);
  const [selectedLocal, setSelectedLocal] = useState<string[]>([]);
  const [discoveredLocal, setDiscoveredLocal] = useState<string[]>([]);
  const [clearOpenai, setClearOpenai] = useState(false);
  const [clearAnthropic, setClearAnthropic] = useState(false);
  const [clearOpenrouter, setClearOpenrouter] = useState(false);
  const [clearLocalLlm, setClearLocalLlm] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);

  const needsTenantPicker = isSuperAdmin && !tenantId;
  const effectiveTenantId = tenantId ?? selectedTenantId;

  useEffect(() => {
    setSelectedTenantId(tenantId);
  }, [tenantId]);

  useEffect(() => {
    if (!needsTenantPicker) {
      return;
    }

    const loadTenants = async () => {
      try {
        setLoadingTenants(true);
        const list = await platformAdminApi.listTenants();
        setTenants(list.map(item => ({ id: item.id, name: item.name })));
        if (list.length === 1) {
          setSelectedTenantId(list[0].id);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Error al listar tenants';
        toast.error(message);
      } finally {
        setLoadingTenants(false);
      }
    };

    loadTenants();
  }, [needsTenantPicker]);

  useEffect(() => {
    if (!effectiveTenantId) {
      setStatus(null);
      onKeysLoaded?.(null);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const data = await chatbotAdminApi.getTenantAiKeys(effectiveTenantId);
        setStatus(data);
        setLocalBaseUrl(data.localLlmBaseUrl ?? '');
        setSelectedOpenRouter(asStringArray(data.openrouterEnabledModels));
        setSelectedLocal(asStringArray(data.localLlmEnabledModels));
        onKeysLoaded?.(data);
      } catch (error: unknown) {
        setStatus(null);
        onKeysLoaded?.(null);
        const message = error instanceof Error ? error.message : 'Error al cargar API keys';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [effectiveTenantId, onKeysLoaded]);

  const selectedTenantName = useMemo(() => {
    if (!effectiveTenantId) {
      return null;
    }
    return tenants.find(t => t.id === effectiveTenantId)?.name ?? status?.tenantName;
  }, [effectiveTenantId, tenants, status?.tenantName]);

  const visibleLocalIds = mergeModelIds(selectedLocal, discoveredLocal);

  const toggleOpenRouter = (id: string) => {
    setSelectedOpenRouter(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id],
    );
  };

  const toggleLocal = (id: string) => {
    setSelectedLocal(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id],
    );
  };

  const handleProbeLocal = async () => {
    if (!effectiveTenantId) {
      toast.error('Seleccione un tenant');
      return;
    }
    try {
      setProbing(true);
      const modelIds = await chatbotAdminApi.probeLocalLlm(effectiveTenantId, localBaseUrl);
      setDiscoveredLocal(modelIds);
      toast.success(
        modelIds.length > 0
          ? `Se listaron ${modelIds.length} modelos`
          : 'El endpoint respondió pero no devolvió modelos',
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al listar modelos locales';
      toast.error(message);
    } finally {
      setProbing(false);
    }
  };

  const handleSave = async () => {
    if (!effectiveTenantId) {
      toast.error('Seleccione un tenant');
      return;
    }

    try {
      setSaving(true);
      const data = await chatbotAdminApi.updateTenantAiKeys(
        {
          openaiApiKey: openaiKey.trim() || undefined,
          anthropicApiKey: anthropicKey.trim() || undefined,
          openrouterApiKey: openrouterKey.trim() || undefined,
          openrouterEnabledModels: selectedOpenRouter,
          localLlmEnabledModels: selectedLocal,
          clearOpenaiApiKey: clearOpenai || undefined,
          clearAnthropicApiKey: clearAnthropic || undefined,
          clearOpenrouterApiKey: clearOpenrouter || undefined,
          // El endpoint local y su key los define solo el superadmin (el backend
          // los ignora para el resto): apuntan a la red interna del servidor.
          ...(isSuperAdmin
            ? {
                localLlmBaseUrl: localBaseUrl.trim() || null,
                localLlmApiKey: localLlmKey.trim() || undefined,
                clearLocalLlmApiKey: clearLocalLlm || undefined,
              }
            : {}),
        },
        effectiveTenantId,
      );
      setStatus(data);
      setSelectedOpenRouter(asStringArray(data.openrouterEnabledModels));
      setSelectedLocal(asStringArray(data.localLlmEnabledModels));
      setLocalBaseUrl(data.localLlmBaseUrl ?? '');
      onKeysLoaded?.(data);
      setOpenaiKey('');
      setAnthropicKey('');
      setOpenrouterKey('');
      setLocalLlmKey('');
      setClearOpenai(false);
      setClearAnthropic(false);
      setClearOpenrouter(false);
      setClearLocalLlm(false);
      toast.success('Configuración IA del tenant actualizada');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al guardar configuración';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          Los bots sin key propia usan estas credenciales. Whisper (audio) requiere OpenAI. El
          backend tiene que alcanzar Ollama/LM Studio, no el localhost del navegador.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        {needsTenantPicker && (
          <div>
            <Label htmlFor='tenantAiKeysTenant'>Tenant</Label>
            {loadingTenants ? (
              <p className='text-sm text-muted-foreground mt-1'>Cargando tenants...</p>
            ) : (
              <select
                id='tenantAiKeysTenant'
                className='w-full border border-input bg-background rounded-md p-2 mt-1'
                value={selectedTenantId ?? ''}
                onChange={event => setSelectedTenantId(event.target.value || undefined)}
              >
                <option value=''>Seleccionar tenant...</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {!effectiveTenantId ? (
          <p className='text-sm text-muted-foreground'>
            {needsTenantPicker
              ? 'Seleccione un tenant para configurar sus API keys.'
              : 'Tenant no disponible.'}
          </p>
        ) : loading ? (
          <p className='text-sm text-muted-foreground'>Cargando...</p>
        ) : status ? (
          <>
            {selectedTenantName && needsTenantPicker && (
              <p className='text-sm text-muted-foreground'>Tenant: {selectedTenantName}</p>
            )}
            <div className='flex flex-wrap gap-2'>
              <Badge variant={status.openaiApiKey.configured ? 'default' : 'secondary'}>
                OpenAI: {keyStatusLabel(status.openaiApiKey)}
              </Badge>
              <Badge variant={status.anthropicApiKey.configured ? 'default' : 'secondary'}>
                Anthropic: {keyStatusLabel(status.anthropicApiKey)}
              </Badge>
              {status.openrouterApiKey ? (
                <Badge variant={status.openrouterApiKey.configured ? 'default' : 'secondary'}>
                  OpenRouter: {keyStatusLabel(status.openrouterApiKey)}
                </Badge>
              ) : null}
              {status.localConfigured !== undefined ? (
                <Badge variant={status.localConfigured ? 'default' : 'secondary'}>
                  Local: {status.localConfigured ? 'URL configurada' : 'Sin URL'}
                </Badge>
              ) : null}
            </div>

            <div className='grid gap-4 md:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='tenantOpenaiKey'>OpenAI API Key</Label>
                <Input
                  id='tenantOpenaiKey'
                  type='password'
                  value={openaiKey}
                  onChange={event => setOpenaiKey(event.target.value)}
                  placeholder='sk-... (vacío = no cambiar)'
                />
                <label className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <input
                    type='checkbox'
                    checked={clearOpenai}
                    onChange={event => setClearOpenai(event.target.checked)}
                  />
                  Eliminar key OpenAI del tenant
                </label>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='tenantAnthropicKey'>Anthropic API Key</Label>
                <Input
                  id='tenantAnthropicKey'
                  type='password'
                  value={anthropicKey}
                  onChange={event => setAnthropicKey(event.target.value)}
                  placeholder='sk-ant-... (vacío = no cambiar)'
                />
                <label className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <input
                    type='checkbox'
                    checked={clearAnthropic}
                    onChange={event => setClearAnthropic(event.target.checked)}
                  />
                  Eliminar key Anthropic del tenant
                </label>
              </div>
              <div className='space-y-2 md:col-span-2'>
                <Label htmlFor='tenantOpenrouterKey'>OpenRouter API Key</Label>
                <Input
                  id='tenantOpenrouterKey'
                  type='password'
                  value={openrouterKey}
                  onChange={event => setOpenrouterKey(event.target.value)}
                  placeholder='sk-or-... (vacío = no cambiar)'
                />
                <label className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <input
                    type='checkbox'
                    checked={clearOpenrouter}
                    onChange={event => setClearOpenrouter(event.target.checked)}
                  />
                  Eliminar key OpenRouter del tenant
                </label>
              </div>
            </div>

            <div className='space-y-3 rounded-lg border border-border p-4'>
              <div>
                <h3 className='font-medium'>Modelos OpenRouter</h3>
                <p className='text-sm text-muted-foreground mt-1'>
                  Los tildados aparecen al crear bots con proveedor OpenRouter.
                </p>
              </div>
              <div className='grid gap-2 sm:grid-cols-2 max-h-72 overflow-y-auto'>
                {(status.openrouterCatalog ?? []).map(model => (
                  <label
                    key={model.id}
                    className='flex items-start gap-2 rounded-lg border border-border p-3 text-sm'
                  >
                    <input
                      type='checkbox'
                      className='mt-0.5'
                      checked={selectedOpenRouter.includes(model.id)}
                      onChange={() => toggleOpenRouter(model.id)}
                    />
                    <span>
                      <span className='font-medium'>{model.label}</span>
                      <span className='block text-xs text-muted-foreground'>
                        {model.vendor} · {model.id}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {isSuperAdmin || status.localLlmBaseUrl ? (
            <div className='space-y-3 rounded-lg border border-border p-4'>
              <div>
                <h3 className='font-medium'>LLM local (Ollama / LM Studio)</h3>
                <p className='text-sm text-muted-foreground mt-1'>
                  {isSuperAdmin
                    ? 'URL OpenAI-compatible alcanzable por el backend (ej. http://10.0.0.50:1234/v1).'
                    : 'El servidor lo configura el administrador de la plataforma. Elegí qué modelos usar.'}
                </p>
              </div>
              {isSuperAdmin ? (
                <>
                  <div className='grid gap-3 md:grid-cols-[1fr_auto]'>
                    <Input
                      value={localBaseUrl}
                      onChange={event => setLocalBaseUrl(event.target.value)}
                      placeholder='http://127.0.0.1:1234/v1'
                    />
                    <Button type='button' variant='outline' disabled={probing} onClick={() => void handleProbeLocal()}>
                      {probing ? 'Listando...' : 'Listar modelos'}
                    </Button>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='tenantLocalLlmKey'>API key local (opcional)</Label>
                    <Input
                      id='tenantLocalLlmKey'
                      type='password'
                      value={localLlmKey}
                      onChange={event => setLocalLlmKey(event.target.value)}
                      placeholder='Bearer opcional (vacío = no cambiar)'
                    />
                    <label className='flex items-center gap-2 text-sm text-muted-foreground'>
                      <input
                        type='checkbox'
                        checked={clearLocalLlm}
                        onChange={event => setClearLocalLlm(event.target.checked)}
                      />
                      Eliminar key local del tenant
                    </label>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <Input
                      value={customLocalModel}
                      onChange={event => setCustomLocalModel(event.target.value)}
                      placeholder='Id manual (ej. llama3.1:8b)'
                      className='max-w-xs'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        const id = customLocalModel.trim().slice(0, 128);
                        if (!id) return;
                        setSelectedLocal(current => mergeModelIds(current, [id]));
                        setCustomLocalModel('');
                      }}
                    >
                      Agregar modelo
                    </Button>
                  </div>
                </>
              ) : null}
              <div className='grid gap-2 sm:grid-cols-2 max-h-48 overflow-y-auto'>
                {visibleLocalIds.map(id => (
                  <label
                    key={id}
                    className='flex items-center gap-2 rounded-lg border border-border p-2 text-sm'
                  >
                    <input
                      type='checkbox'
                      checked={selectedLocal.includes(id)}
                      onChange={() => toggleLocal(id)}
                    />
                    {id}
                  </label>
                ))}
              </div>
            </div>
            ) : null}

            <div className='flex justify-end'>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar configuración IA'}
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
